import { Box, Skeleton, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/** Skeleton that matches the vertical SectionEmptyState layout. Drop-in replacement while loading. */
export function EmptyStateSkeleton({
  hasDescription = true,
  hasAction = false
}: {
  hasDescription?: boolean;
  hasAction?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, px: 2 }}>
      <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 1.5, mb: 1.5 }} />
      <Skeleton variant="text" width={160} height={22} />
      {hasDescription && <Skeleton variant="text" width={220} height={18} sx={{ mt: 0.5 }} />}
      {hasAction && (
        <Skeleton variant="rounded" width={100} height={32} sx={{ mt: 2, borderRadius: 1 }} />
      )}
    </Box>
  );
}

interface SectionEmptyStateProps {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
}

export default function SectionEmptyState({
  icon,
  title,
  description,
  action,
  orientation = 'vertical'
}: SectionEmptyStateProps) {
  if (orientation === 'horizontal') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          {description && (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        {action && (
          <Box sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}>{action}</Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 3,
        px: 2
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: action ? 2 : 0, display: 'block' }}
        >
          {description}
        </Typography>
      )}
      {action}
    </Box>
  );
}
